import dayjs from "dayjs";
import {
  ClientIdentifiedEvent,
  ReviewPeriodData,
  ReviewSlotRevision,
} from "src/store/api/generatedApi";
import { canonicalInstant } from "./eventUtils";
import { ReviewRefreshRequiredError } from "./reviewErrors";

const slotContainsStart = (
  revision: ReviewSlotRevision,
  start: string
): boolean =>
  !dayjs.utc(start).isBefore(dayjs.utc(revision.start)) &&
  dayjs.utc(start).isBefore(dayjs.utc(revision.end));

export const expectedSlotRevisions = ({
  snapshots,
  periods,
  correctedEvents,
  verifyPeriodStarts,
}: {
  snapshots: (ReviewSlotRevision[] | undefined)[];
  periods: ReviewPeriodData[];
  correctedEvents: ClientIdentifiedEvent[];
  verifyPeriodStarts: string[];
}): ReviewSlotRevision[] | undefined => {
  if (snapshots.every((snapshot) => snapshot === undefined)) return undefined;
  const requestedStarts = [
    ...periods.map((period) => period.start),
    ...verifyPeriodStarts,
  ];
  const correctedStarts = correctedEvents.map((event) => event.event.start);
  const isRequested = (revision: ReviewSlotRevision): boolean =>
    requestedStarts.some(
      (start) => canonicalInstant(start) === canonicalInstant(revision.start)
    ) || correctedStarts.some((start) => slotContainsStart(revision, start));
  const byStart = snapshots
    .flatMap((snapshot) => snapshot ?? [])
    .filter(isRequested)
    .reduce((revisions, revision) => {
      const key = canonicalInstant(revision.start);
      const existing = revisions.get(key);
      if (
        existing &&
        (existing.revision_id !== revision.revision_id ||
          canonicalInstant(existing.end) !== canonicalInstant(revision.end))
      ) {
        throw new ReviewRefreshRequiredError(
          "Review slots changed while loading events"
        );
      }
      revisions.set(key, revision);
      return revisions;
    }, new Map<string, ReviewSlotRevision>());
  const selected = [...byStart.values()];
  if (
    requestedStarts.some(
      (start) =>
        !selected.some(
          (revision) =>
            canonicalInstant(start) === canonicalInstant(revision.start)
        )
    ) ||
    correctedStarts.some(
      (start) =>
        !selected.some((revision) => slotContainsStart(revision, start))
    )
  ) {
    throw new ReviewRefreshRequiredError(
      "Review revision snapshot does not cover this save"
    );
  }
  return selected;
};

export const revisionToUnverify = ({
  period,
  snapshot,
}: {
  period: ReviewPeriodData;
  snapshot: ReviewSlotRevision[] | undefined;
}): number => {
  if (snapshot === undefined) return period.id;
  const revision = snapshot.find(
    (candidate) =>
      canonicalInstant(candidate.start) === canonicalInstant(period.start)
  );
  if (revision?.revision_id == null)
    throw new ReviewRefreshRequiredError(
      "Review revision snapshot does not cover this unverify"
    );
  return revision.revision_id;
};
